/**
 * AppPanelTaskbar Component
 *
 * Windows-style taskbar at the bottom of the screen for minimized AppPanels.
 * Appears only when there are minimized panels.
 *
 * Features:
 * - Fixed at very bottom of screen
 * - Magic UI-style magnification on hover (macOS dock effect)
 * - Click to restore panel
 * - Close button to remove panel
 * - Right-click context menu
 * - Smooth show/hide animation
 * - Exports --taskbar-height CSS variable for layout coordination
 */

import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { X, ExternalLink, Maximize2 } from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { useAppPanelStore, type MinimizedAppPanel } from '@shared/store/useAppPanelStore';
import { useUIStore } from '@shared/store/uiStore';

/** Taskbar height in pixels */
const TASKBAR_HEIGHT = 48;

/** Magnification settings */
const MAGNIFICATION = {
  baseSize: 40,
  maxSize: 52,
  distance: 100, // pixels - how far the magnification effect extends
};

/**
 * Context menu position
 */
interface ContextMenuPosition {
  x: number;
  y: number;
  panelId: string;
}

/**
 * Individual taskbar item with magnification effect
 */
interface TaskbarItemProps {
  panel: MinimizedAppPanel;
  mouseX: ReturnType<typeof useMotionValue<number>>;
  onRestore: () => void;
  onClose: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  isDynamicsTheme: boolean;
}

const TaskbarItem: React.FC<TaskbarItemProps> = ({
  panel,
  mouseX,
  onRestore,
  onClose,
  onContextMenu,
  isDynamicsTheme,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Calculate distance from mouse to this item's center
  const distance = useTransform(mouseX, (val) => {
    if (!ref.current || val === 0) return MAGNIFICATION.distance + 1;
    const rect = ref.current.getBoundingClientRect();
    const itemCenterX = rect.left + rect.width / 2;
    return Math.abs(val - itemCenterX);
  });

  // Scale based on distance - closer = larger (macOS dock effect)
  const scale = useTransform(distance, [0, MAGNIFICATION.distance], [1.15, 1]);
  const springScale = useSpring(scale, { stiffness: 400, damping: 25 });

  // Size transform for the icon area
  const size = useTransform(
    springScale,
    [1, 1.15],
    [MAGNIFICATION.baseSize, MAGNIFICATION.maxSize]
  );

  // Truncate title
  const displayTitle = panel.title.length > 16 ? `${panel.title.slice(0, 16)}...` : panel.title;

  return (
    <motion.div
      ref={ref}
      className={`
        relative flex items-center gap-2 rounded-lg px-3 cursor-pointer
        transition-colors duration-150
        ${isDynamicsTheme
          ? 'bg-dark-surface/80 hover:bg-dark-surface border border-dark-border/30'
          : 'bg-dark-surface/80 hover:bg-dark-surface border border-dark-border/30'
        }
      `}
      style={{ height: size }}
      onClick={onRestore}
      onContextMenu={onContextMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      aria-label={`Restore ${panel.title}`}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onRestore()}
      data-testid={`taskbar-item-${panel.id}`}
      initial={{ opacity: 0, y: 20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.8 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {/* App icon */}
      <div
        className={`
          flex-shrink-0 w-6 h-6 rounded flex items-center justify-center
          ${isDynamicsTheme ? 'bg-brand-500/20 text-brand-400' : 'bg-brand-500/20 text-brand-400'}
        `}
      >
        <ExternalLink size={14} />
      </div>

      {/* Title */}
      <span
        className="text-xs font-medium text-dark-text truncate max-w-[100px]"
        title={panel.title}
      >
        {displayTitle}
      </span>

      {/* Close button - appears on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.button
            type="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.1 }}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className={`
              flex-shrink-0 p-1 rounded transition-colors
              text-dark-muted hover:text-red-400 hover:bg-red-500/10
            `}
            aria-label={`Close ${panel.title}`}
            data-testid={`taskbar-close-${panel.id}`}
          >
            <X size={12} />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Context menu for taskbar items
 */
interface TaskbarContextMenuProps {
  position: ContextMenuPosition;
  panel: MinimizedAppPanel;
  onClose: () => void;
  onRestore: () => void;
  onRemove: () => void;
  isDynamicsTheme: boolean;
}

const TaskbarContextMenu: React.FC<TaskbarContextMenuProps> = ({
  position,
  panel,
  onClose,
  onRestore,
  onRemove,
  isDynamicsTheme,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside or escape
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <Portal>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95, y: 5 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 5 }}
        transition={{ duration: 0.1 }}
        className={`
          fixed z-[200] rounded-lg shadow-2xl shadow-black/30 border py-1 min-w-[140px]
          ${isDynamicsTheme
            ? 'bg-dark-surface border-dark-border/30'
            : 'bg-dark-surface border-dark-border/30'
          }
        `}
        style={{
          left: Math.min(position.x, window.innerWidth - 160),
          top: Math.max(position.y - 80, 10), // Position above click point (since taskbar is at bottom)
        }}
        role="menu"
        aria-label={`Actions for ${panel.title}`}
      >
        <button
          type="button"
          onClick={() => {
            onRestore();
            onClose();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-dark-text hover:bg-dark-input"
          role="menuitem"
        >
          <Maximize2 size={14} />
          Restore
        </button>
        <button
          type="button"
          onClick={() => {
            onRemove();
            onClose();
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-400 hover:bg-red-900/20"
          role="menuitem"
        >
          <X size={14} />
          Close
        </button>
      </motion.div>
    </Portal>
  );
};

/**
 * AppPanelTaskbar - Bottom taskbar for minimized app panels
 *
 * IMPORTANT: This component is designed to be part of the AppShell flex layout,
 * NOT a Portal overlay. This ensures all content naturally pushes up when visible.
 */
export const AppPanelTaskbar: React.FC = () => {
  const { minimizedPanels, isTaskbarVisible, restoreMinimizedPanel, closeMinimizedPanel } = useAppPanelStore();
  const themeClass = useUIStore((state) => state.themeClass);
  const isDynamicsTheme = themeClass === 'theme-dynamics';

  // Mouse position for magnification effect
  const mouseX = useMotionValue(0);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);

  // Handle mouse move for magnification
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    mouseX.set(e.clientX);
  }, [mouseX]);

  // Reset mouse position when leaving taskbar
  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
  }, [mouseX]);

  // Handle context menu
  const handleContextMenu = useCallback((e: React.MouseEvent, panelId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, panelId });
  }, []);

  // Get panel for context menu
  const contextMenuPanel = contextMenu
    ? minimizedPanels.find((p) => p.id === contextMenu.panelId)
    : null;

  // Don't render if no minimized panels or inside iframe
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  if (!isTaskbarVisible || minimizedPanels.length === 0 || isInIframe) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: TASKBAR_HEIGHT, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className={`
            shrink-0 w-full
            flex items-center justify-center gap-2 px-4
            bg-dark-bg/95 backdrop-blur-md
            border-t border-dark-border/30
          `}
          style={{
            // Export CSS variable for floating components that need to know taskbar height
            ['--taskbar-height' as string]: `${TASKBAR_HEIGHT}px`,
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          role="toolbar"
          aria-label="Minimized app panels"
          data-testid="app-panel-taskbar"
        >
          <AnimatePresence mode="popLayout">
            {minimizedPanels.map((panel) => (
              <TaskbarItem
                key={panel.id}
                panel={panel}
                mouseX={mouseX}
                onRestore={() => restoreMinimizedPanel(panel.id)}
                onClose={() => closeMinimizedPanel(panel.id)}
                onContextMenu={(e) => handleContextMenu(e, panel.id)}
                isDynamicsTheme={isDynamicsTheme}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>

      {/* Context Menu - uses Portal since it's an overlay */}
      <AnimatePresence>
        {contextMenu && contextMenuPanel && (
          <Portal>
            <TaskbarContextMenu
              position={contextMenu}
              panel={contextMenuPanel}
              onClose={() => setContextMenu(null)}
              onRestore={() => restoreMinimizedPanel(contextMenu.panelId)}
              onRemove={() => closeMinimizedPanel(contextMenu.panelId)}
              isDynamicsTheme={isDynamicsTheme}
            />
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
};

/** Export taskbar height for layout calculations */
export const APP_PANEL_TASKBAR_HEIGHT = TASKBAR_HEIGHT;

export default AppPanelTaskbar;
